'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PValueCalculator() {
  const t = useTranslations("tools.pValueCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    zScore: '',
    pLeft: '',
    pRight: '',
    pCenter: '',
    pBetween: '',
    pTwoTail: ''
  });

  const [results, setResults] = useState<{
    z: number;
    pLeft: number;
    pRight: number;
    pCenter: number;
    pBetween: number;
    pTwoTail: number;
  } | null>(null);

  const [, setActiveInput] = useState<string | null>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  // Statistical functions
  const erf = (x: number): number => {
    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const sign = x < 0 ? -1 : 1;
    x = Math.abs(x);

    const t = 1.0 / (1.0 + p * x);
    const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  const normalCDF = (z: number): number => {
    return 0.5 * (1 + erf(z / Math.sqrt(2)));
  };

  const inverseNormalCDF = (p: number): number | null => {
    if (p <= 0 || p >= 1) return null;

    const a = [
      -3.969683028665376e+01, 2.209460984245205e+02,
      -2.759285104469687e+02, 1.383577518672690e+02,
      -3.066479806614716e+01, 2.506628277459239e+00
    ];
    const b = [
      -5.447609879822406e+01, 1.615858368580409e+02,
      -1.556989798598866e+02, 6.680131188771972e+01,
      -1.328068155288572e+01
    ];
    const c = [
      -7.784894002430293e-03, -3.223964580411365e-01,
      -2.400758277161838e+00, -2.549732539343734e+00,
      4.374664141464968e+00, 2.938163982698783e+00
    ];
    const d = [
      7.784695709041462e-03, 3.224671290700398e-01,
      2.445134137142996e+00, 3.754408661907416e+00
    ];

    const pLow = 0.02425;
    const pHigh = 1 - pLow;

    let q: number, r: number, z: number;

    if (p < pLow) {
      q = Math.sqrt(-2 * Math.log(p));
      z = (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
          ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    } else if (p <= pHigh) {
      q = p - 0.5;
      r = q * q;
      z = (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q /
          (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
    } else {
      q = Math.sqrt(-2 * Math.log(1 - p));
      z = -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
          ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
    }

    return z;
  };

  const calculateFromZ = (z: number) => {
    const pLeft = normalCDF(z);
    const pRight = 1 - pLeft;
    const pCenter = Math.abs(pLeft - 0.5);
    const pBetween = 2 * pCenter;
    const pTwoTail = 2 * Math.min(pLeft, pRight);

    return {
      z: z,
      pLeft: pLeft,
      pRight: pRight,
      pCenter: pCenter,
      pBetween: pBetween,
      pTwoTail: pTwoTail
    };
  };

  const calculateFromPLeft = (p: number) => {
    const z = inverseNormalCDF(p);
    if (z === null) return null;
    return calculateFromZ(z);
  };

  const calculateFromPRight = (p: number) => {
    const z = inverseNormalCDF(1 - p);
    if (z === null) return null;
    return calculateFromZ(z);
  };

  const calculateFromPCenter = (p: number) => {
    if (p < 0 || p > 0.5) return null;
    const pLeft = 0.5 + p;
    const z = inverseNormalCDF(pLeft);
    if (z === null) return null;
    return calculateFromZ(z);
  };

  const calculateFromPBetween = (p: number) => {
    if (p < 0 || p > 1) return null;
    const pCenter = p / 2;
    return calculateFromPCenter(pCenter);
  };

  const calculateFromPTwoTail = (p: number) => {
    if (p < 0 || p > 1) return null;
    const pRight = p / 2;
    const z = inverseNormalCDF(1 - pRight);
    if (z === null) return null;
    return calculateFromZ(z);
  };

  const handleInputChange = (field: string, value: string) => {
    setInputs({
      zScore: '',
      pLeft: '',
      pRight: '',
      pCenter: '',
      pBetween: '',
      pTwoTail: '',
      [field]: value
    });
    setActiveInput(field);
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let calculatedResults = null;

    if (inputs.zScore) {
      calculatedResults = calculateFromZ(parseFloat(inputs.zScore));
    } else if (inputs.pLeft) {
      calculatedResults = calculateFromPLeft(parseFloat(inputs.pLeft));
    } else if (inputs.pRight) {
      calculatedResults = calculateFromPRight(parseFloat(inputs.pRight));
    } else if (inputs.pCenter) {
      calculatedResults = calculateFromPCenter(parseFloat(inputs.pCenter));
    } else if (inputs.pBetween) {
      calculatedResults = calculateFromPBetween(parseFloat(inputs.pBetween));
    } else if (inputs.pTwoTail) {
      calculatedResults = calculateFromPTwoTail(parseFloat(inputs.pTwoTail));
    }

    if (calculatedResults) {
      setResults(calculatedResults);
    }
  };

  const handleClear = () => {
    setInputs({
      zScore: '',
      pLeft: '',
      pRight: '',
      pCenter: '',
      pBetween: '',
      pTwoTail: ''
    });
    setResults(null);
    setActiveInput(null);
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, maxWidth: '800px', margin: '0 auto' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
      borderRadius: '1rem 1rem 0 0',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.65rem' },
    { fontSize: '1.8rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {
      marginBottom: '1.5rem',
      display: 'grid',
      gap: '1rem',
      alignItems: 'center' as const
    },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 2fr 80px' },
    { gridTemplateColumns: '1fr 2fr 80px' }
  );

  const labelStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary },
    { fontSize: '0.9rem', marginBottom: '0.5rem', display: 'block' },
    { fontSize: '0.95rem', marginBottom: 0 },
    { fontSize: '0.95rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `2px solid #e2e8f0`,
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', justifyContent: 'center' as const, marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      background: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    { padding: '0.875rem 2rem', fontSize: '1rem', width: '100%' },
    { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' },
    { padding: '0.875rem 2.5rem', fontSize: '1rem', width: 'auto' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderRadius: '1rem',
      border: '2px solid #86efac',
      marginTop: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultItemStyle = getResponsiveStyle(
    {
      display: 'grid',
      background: 'white',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    { gridTemplateColumns: '1fr', gap: '0.5rem', padding: '0.875rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '1rem' },
    { gridTemplateColumns: '1fr 1fr', gap: '1rem', padding: '1rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(37, 99, 235, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const infoBoxStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderRadius: '0.5rem',
      borderLeft: '4px solid #f59e0b',
      marginTop: '2rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const tailIconContainerStyle: React.CSSProperties = {
    width: '60px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={toolTitleStyle}>{t("toolTitle")}</h2>
        </div>

        <form onSubmit={handleCalculate} style={{ maxWidth: '900px', margin: '0 auto', background: 'white', borderRadius: '1rem', padding: screenSize === 'mobile' ? '1.5rem' : '2rem' }}>
          <div style={formGroupStyle}>
            <label htmlFor="z-score" style={labelStyle}>{t("zScoreLabel")}</label>
            <input
              type="number"
              id="z-score"
              step="0.0001"
              placeholder={t("zScorePlaceholder")}
              value={inputs.zScore}
              onChange={(e) => handleInputChange('zScore', e.target.value)}
              style={inputStyle}
            />
            {screenSize !== 'mobile' && <div style={tailIconContainerStyle}></div>}
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="p-left" style={labelStyle}>{t("pLeftLabel")}</label>
            <input
              type="number"
              id="p-left"
              step="0.0001"
              min="0"
              max="1"
              placeholder={t("pLeftPlaceholder")}
              value={inputs.pLeft}
              onChange={(e) => handleInputChange('pLeft', e.target.value)}
              style={inputStyle}
            />
            {screenSize !== 'mobile' && (
              <div style={tailIconContainerStyle}>
                <svg viewBox="0 0 100 60" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                  <path d="M 10 50 Q 30 10, 50 30 Q 70 50, 90 30" stroke="#2563eb" fill="none" strokeWidth="2"/>
                  <path d="M 10 50 Q 30 10, 50 30 L 50 50 Z" fill="#2563eb" opacity="0.3"/>
                  <line x1="50" y1="30" x2="50" y2="50" stroke="#2563eb" strokeWidth="2" strokeDasharray="2,2"/>
                </svg>
              </div>
            )}
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="p-right" style={labelStyle}>{t("pRightLabel")}</label>
            <input
              type="number"
              id="p-right"
              step="0.0001"
              min="0"
              max="1"
              placeholder={t("pRightPlaceholder")}
              value={inputs.pRight}
              onChange={(e) => handleInputChange('pRight', e.target.value)}
              style={inputStyle}
            />
            {screenSize !== 'mobile' && (
              <div style={tailIconContainerStyle}>
                <svg viewBox="0 0 100 60" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                  <path d="M 10 50 Q 30 10, 50 30 Q 70 50, 90 30" stroke="#7c3aed" fill="none" strokeWidth="2"/>
                  <path d="M 50 30 Q 70 50, 90 30 L 90 50 L 50 50 Z" fill="#7c3aed" opacity="0.3"/>
                  <line x1="50" y1="30" x2="50" y2="50" stroke="#7c3aed" strokeWidth="2" strokeDasharray="2,2"/>
                </svg>
              </div>
            )}
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="p-center" style={labelStyle}>{t("pCenterLabel")}</label>
            <input
              type="number"
              id="p-center"
              step="0.0001"
              min="0"
              max="0.5"
              placeholder={t("pCenterPlaceholder")}
              value={inputs.pCenter}
              onChange={(e) => handleInputChange('pCenter', e.target.value)}
              style={inputStyle}
            />
            {screenSize !== 'mobile' && (
              <div style={tailIconContainerStyle}>
                <svg viewBox="0 0 100 60" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                  <path d="M 10 50 Q 30 10, 50 30 Q 70 50, 90 30" stroke="#10b981" fill="none" strokeWidth="2"/>
                  <path d="M 50 30 L 50 50 L 70 50 Q 65 40, 70 30 Z" fill="#10b981" opacity="0.3"/>
                  <line x1="50" y1="30" x2="50" y2="50" stroke="#10b981" strokeWidth="2" strokeDasharray="2,2"/>
                </svg>
              </div>
            )}
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="p-between" style={labelStyle}>{t("pBetweenLabel")}</label>
            <input
              type="number"
              id="p-between"
              step="0.0001"
              min="0"
              max="1"
              placeholder={t("pBetweenPlaceholder")}
              value={inputs.pBetween}
              onChange={(e) => handleInputChange('pBetween', e.target.value)}
              style={inputStyle}
            />
            {screenSize !== 'mobile' && (
              <div style={tailIconContainerStyle}>
                <svg viewBox="0 0 100 60" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                  <path d="M 10 50 Q 30 10, 50 30 Q 70 50, 90 30" stroke="#f59e0b" fill="none" strokeWidth="2"/>
                  <path d="M 30 40 Q 35 20, 50 30 Q 65 20, 70 40 L 70 50 L 30 50 Z" fill="#f59e0b" opacity="0.3"/>
                  <line x1="30" y1="40" x2="30" y2="50" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="2,2"/>
                  <line x1="70" y1="40" x2="70" y2="50" stroke="#f59e0b" strokeWidth="1.5" strokeDasharray="2,2"/>
                </svg>
              </div>
            )}
          </div>

          <div style={formGroupStyle}>
            <label htmlFor="p-two-tail" style={labelStyle}>{t("pTwoTailLabel")}</label>
            <input
              type="number"
              id="p-two-tail"
              step="0.0001"
              min="0"
              max="1"
              placeholder={t("pTwoTailPlaceholder")}
              value={inputs.pTwoTail}
              onChange={(e) => handleInputChange('pTwoTail', e.target.value)}
              style={inputStyle}
            />
            {screenSize !== 'mobile' && (
              <div style={tailIconContainerStyle}>
                <svg viewBox="0 0 100 60" xmlns="http://www.w3.org/2000/svg" style={{ width: '100%', height: '100%' }}>
                  <path d="M 10 50 Q 30 10, 50 30 Q 70 50, 90 30" stroke="#ef4444" fill="none" strokeWidth="2"/>
                  <path d="M 10 50 Q 30 10, 30 40 L 30 50 Z" fill="#ef4444" opacity="0.3"/>
                  <path d="M 70 40 Q 70 50, 90 30 L 90 50 L 70 50 Z" fill="#ef4444" opacity="0.3"/>
                  <line x1="30" y1="40" x2="30" y2="50" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="2,2"/>
                  <line x1="70" y1="40" x2="70" y2="50" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="2,2"/>
                </svg>
              </div>
            )}
          </div>

          <div style={buttonGroupStyle}>
            <button type="submit" style={calculateButtonStyle}>{t("calculateButton")}</button>
            <button type="button" onClick={handleClear} style={clearButtonStyle}>{t("clearButton")}</button>
          </div>

          {results && (
            <div style={resultsStyle}>
              <h3 style={{ margin: '0 0 1.5rem 0', color: '#166534', fontSize: screenSize === 'mobile' ? '1.25rem' : '1.3rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                📈 {t("resultsTitle")}
              </h3>
              <div style={resultItemStyle}>
                <span style={{ fontWeight: 600, color: '#475569', fontSize: screenSize === 'mobile' ? '0.9rem' : '0.95rem' }}>{t("resultZScore")}</span>
                <span style={{ fontWeight: 700, color: '#2563eb', fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem', textAlign: screenSize === 'mobile' ? 'left' : 'right' }}>{results.z.toFixed(6)}</span>
              </div>
              <div style={resultItemStyle}>
                <span style={{ fontWeight: 600, color: '#475569', fontSize: screenSize === 'mobile' ? '0.9rem' : '0.95rem' }}>{t("resultPLeft")}</span>
                <span style={{ fontWeight: 700, color: '#2563eb', fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem', textAlign: screenSize === 'mobile' ? 'left' : 'right' }}>{results.pLeft.toFixed(6)}</span>
              </div>
              <div style={resultItemStyle}>
                <span style={{ fontWeight: 600, color: '#475569', fontSize: screenSize === 'mobile' ? '0.9rem' : '0.95rem' }}>{t("resultPRight")}</span>
                <span style={{ fontWeight: 700, color: '#2563eb', fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem', textAlign: screenSize === 'mobile' ? 'left' : 'right' }}>{results.pRight.toFixed(6)}</span>
              </div>
              <div style={resultItemStyle}>
                <span style={{ fontWeight: 600, color: '#475569', fontSize: screenSize === 'mobile' ? '0.9rem' : '0.95rem' }}>{t("resultPCenter")}</span>
                <span style={{ fontWeight: 700, color: '#2563eb', fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem', textAlign: screenSize === 'mobile' ? 'left' : 'right' }}>{results.pCenter.toFixed(6)}</span>
              </div>
              <div style={resultItemStyle}>
                <span style={{ fontWeight: 600, color: '#475569', fontSize: screenSize === 'mobile' ? '0.9rem' : '0.95rem' }}>{t("resultPBetween")}</span>
                <span style={{ fontWeight: 700, color: '#2563eb', fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem', textAlign: screenSize === 'mobile' ? 'left' : 'right' }}>{results.pBetween.toFixed(6)}</span>
              </div>
              <div style={resultItemStyle}>
                <span style={{ fontWeight: 600, color: '#475569', fontSize: screenSize === 'mobile' ? '0.9rem' : '0.95rem' }}>{t("resultPTwoTail")}</span>
                <span style={{ fontWeight: 700, color: '#2563eb', fontSize: screenSize === 'mobile' ? '1rem' : '1.1rem', textAlign: screenSize === 'mobile' ? 'left' : 'right' }}>{results.pTwoTail.toFixed(6)}</span>
              </div>
            </div>
          )}

          <div style={infoBoxStyle}>
            <p style={{ margin: 0, color: '#78350f', lineHeight: 1.6, fontSize: screenSize === 'mobile' ? '0.9rem' : '1rem' }}>
              <strong>💡 {t("tipTitle")}</strong> {t("tipText")}
            </p>
          </div>
        </form>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introParagraph1")}</p>
        <p style={paragraphStyle}>{t("introParagraph2")}</p>
        <p style={paragraphStyle}>{t("introParagraph3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("howToUseStep4")}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <h3 style={h3Style}>{t("insightsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("insightsParagraph1")}</p>
        <p style={paragraphStyle}>{t("insightsParagraph2")}</p>

        <h3 style={h3Style}>{t("insightsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("insightsParagraph3")}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}>{t("insightsBullet1")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("insightsBullet2")}</li>
          <li style={{ marginBottom: '0.5rem' }}>{t("insightsBullet3")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("detailsTitle")}</h2>
        <h3 style={h3Style}>{t("detailsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph1")}</p>

        <h3 style={h3Style}>{t("detailsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph2")}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("testTypeZTest")}</strong> {t("testTypeZTestDesc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("testTypeTTest")}</strong> {t("testTypeTTestDesc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("testTypeChiSquare")}</strong> {t("testTypeChiSquareDesc")}</li>
        </ul>

        <h3 style={h3Style}>{t("detailsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("detailsParagraph3")}</p>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("tailTypeLeftTail")}</strong> {t("tailTypeLeftTailDesc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("tailTypeRightTail")}</strong> {t("tailTypeRightTailDesc")}</li>
          <li style={{ marginBottom: '0.5rem' }}><strong>{t("tailTypeTwoTail")}</strong> {t("tailTypeTwoTailDesc")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("bestPracticesTitle")}</h2>
        <ul style={listStyle}>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice1")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice2")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice3")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice4")}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t("bestPractice5")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <h3 style={h3Style}>{t("faq1Question")}</h3>
        <p style={paragraphStyle}>{t("faq1Answer")}</p>

        <h3 style={h3Style}>{t("faq2Question")}</h3>
        <p style={paragraphStyle}>{t("faq2Answer")}</p>

        <h3 style={h3Style}>{t("faq3Question")}</h3>
        <p style={paragraphStyle}>{t("faq3Answer")}</p>

        <h3 style={h3Style}>{t("faq4Question")}</h3>
        <p style={paragraphStyle}>{t("faq4Answer")}</p>

        <h3 style={h3Style}>{t("faq5Question")}</h3>
        <p style={paragraphStyle}>{t("faq5Answer")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={{ ...listStyle, listStyle: 'none', marginLeft: 0 }}>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.gigacalculator.com/calculators/p-value-significance-calculator.php" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}>
              {t("reference1")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.omnicalculator.com/statistics/p-value" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}>
              {t("reference2")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.simplypsychology.org/p-value.html" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}>
              {t("reference3")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.graphpad.com/quickcalcs/pvalue1/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}>
              {t("reference4")}
            </a>
          </li>
          <li style={{ marginBottom: '0.75rem' }}>
            <a href="https://www.scribbr.com/statistics/p-value/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none', borderBottom: `1px solid ${colors.primary}` }}>
              {t("reference5")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}